import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  IconArrowUp,
  IconArrowDown,
  IconTrash,
  IconPencil,
  IconPlus,
} from '@tabler/icons-react';
import type { TaskDefinition, TaskFieldCondition, TaskSectionDefinition } from '@techgitter/contracts';
import { slugify } from '../../utils/slugify';
import { ConditionEditor, type ConditionTaskOption } from '../shared/ConditionEditor';
import { EditorCard } from '../admin/EditorCard';
import { FormField } from '../panel/FormField';
import { PRIMARY_BTN_SX } from '../../styles/designSystem';
import type { FieldCondition } from '../../types/ticketTypeTemplate';
import { useTaskDropdowns } from '../../hooks/useTaskDropdowns';
import { TaskDropdownFormDialog } from './TaskDropdownFormDialog';

const CANCEL_BTN_SX = { color: '#888', '&:hover': { color: '#555', bgcolor: 'transparent' } };

interface SectionEditorProps {
  section: TaskSectionDefinition;
  onSectionChange: (updated: TaskSectionDefinition) => void;
  onDelete: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** Every task in this task list — the pickable sources for `taskOutcome`
   *  conditions (same-list scope). Includes this section's own tasks. */
  availableTasks?: TaskDefinition[];
}

export const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onSectionChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  availableTasks = [],
}) => {
  const [expanded, setExpanded] = useState(true);
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  const [addingTask, setAddingTask] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { dropdownCatalog } = useTaskDropdowns();

  // Sibling tasks offered as taskOutcome sources, each with its resolved
  // dropdown options (for the outcome-value picker).
  const taskOptions: ConditionTaskOption[] = React.useMemo(() => {
    const dropdownMap = new Map(
      dropdownCatalog.dropdowns.map((d) => [d.dropdownId, d.options]),
    );
    return availableTasks.map((t) => ({
      taskId: t.taskId,
      title: t.title,
      dropdownOptions: t.dropdownId ? dropdownMap.get(t.dropdownId) : undefined,
    }));
  }, [availableTasks, dropdownCatalog]);

  const handleLabelChange = (label: string) => {
    onSectionChange({ ...section, label });
  };

  const handleShowWhenChange = (next: FieldCondition | null | undefined) => {
    if (next == null) {
      const { showWhen: _omit, ...rest } = section;
      onSectionChange(rest as TaskSectionDefinition);
    } else {
      onSectionChange({ ...section, showWhen: next as TaskFieldCondition });
    }
  };

  const handleTaskChange = (index: number, updated: TaskDefinition) => {
    const tasks = [...section.tasks];
    tasks[index] = updated;
    onSectionChange({ ...section, tasks });
  };

  const handleTaskDelete = (index: number) => {
    const tasks = section.tasks.filter((_, i) => i !== index);
    onSectionChange({ ...section, tasks });
  };

  const handleTaskAdd = (task: TaskDefinition) => {
    onSectionChange({ ...section, tasks: [...section.tasks, task] });
    setAddingTask(false);
  };

  const handleTaskMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= section.tasks.length) return;

    const tasks = [...section.tasks];
    [tasks[index], tasks[targetIndex]] = [tasks[targetIndex], tasks[index]];
    onSectionChange({ ...section, tasks });
  };

  return (
    <>
      <EditorCard
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        title={
          <>
            <span>{section.label}</span>
            <span className="editor-count">{section.tasks.length} tasks</span>
          </>
        }
        actions={
          <>
            {onMoveUp && (
              <button type="button" className="editor-icon-btn" title="Move up" onClick={onMoveUp}>
                <IconArrowUp size={15} stroke={1.5} />
              </button>
            )}
            {onMoveDown && (
              <button type="button" className="editor-icon-btn" title="Move down" onClick={onMoveDown}>
                <IconArrowDown size={15} stroke={1.5} />
              </button>
            )}
            <button
              type="button"
              className="editor-icon-btn editor-icon-btn--danger"
              title="Delete section"
              onClick={() => setDeleteConfirm(true)}
            >
              <IconTrash size={15} stroke={1.5} />
            </button>
          </>
        }
      >
        <div className="editor-field-row">
          <FormField label="Section Label">
            <input
              type="text"
              value={section.label}
              onChange={(e) => handleLabelChange(e.target.value)}
            />
          </FormField>
          <FormField label="Section ID">
            <input
              type="text"
              value={section.sectionId}
              readOnly
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            />
          </FormField>
        </div>

        <ConditionEditor
          value={section.showWhen}
          onChange={handleShowWhenChange}
          allowCustom={false}
          allowTaskOutcome
          taskOptions={taskOptions}
          label="Section Show When"
        />

        {section.tasks.length === 0 ? (
          <div className="editor-empty">No tasks in this section.</div>
        ) : (
          <div className="editor-rows">
            {section.tasks.map((task, index) => (
              <React.Fragment key={task.taskId}>
                <div className="editor-row">
                  <div className="editor-row__main">
                    <div className="editor-row__title">{task.title}</div>
                    <div className="editor-row__caption">
                      {task.groupLabel ? `${task.groupLabel} · ` : ''}
                      <span className="editor-row__caption-mono">{task.taskId}</span>
                    </div>
                  </div>
                  <div className="editor-row__actions">
                    {index > 0 && (
                      <button
                        type="button"
                        className="editor-icon-btn"
                        title="Move up"
                        onClick={() => handleTaskMove(index, 'up')}
                      >
                        <IconArrowUp size={15} stroke={1.5} />
                      </button>
                    )}
                    {index < section.tasks.length - 1 && (
                      <button
                        type="button"
                        className="editor-icon-btn"
                        title="Move down"
                        onClick={() => handleTaskMove(index, 'down')}
                      >
                        <IconArrowDown size={15} stroke={1.5} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="editor-icon-btn"
                      title="Edit"
                      onClick={() => setEditingTaskIndex(editingTaskIndex === index ? null : index)}
                    >
                      <IconPencil size={15} stroke={1.5} />
                    </button>
                    <button
                      type="button"
                      className="editor-icon-btn editor-icon-btn--danger"
                      title="Delete"
                      onClick={() => handleTaskDelete(index)}
                    >
                      <IconTrash size={15} stroke={1.5} />
                    </button>
                  </div>
                </div>
                {editingTaskIndex === index && (
                  <TaskEditForm
                    task={task}
                    taskOptions={taskOptions}
                    onSave={(updated) => {
                      handleTaskChange(index, updated);
                      setEditingTaskIndex(null);
                    }}
                    onCancel={() => setEditingTaskIndex(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {addingTask ? (
          <TaskEditForm taskOptions={taskOptions} onSave={handleTaskAdd} onCancel={() => setAddingTask(false)} />
        ) : (
          <Button
            variant="text"
            startIcon={<IconPlus size={16} stroke={1.5} />}
            onClick={() => setAddingTask(true)}
            sx={{ alignSelf: 'flex-start', color: '#4A90D9', '&:hover': { bgcolor: '#EDF5FF' } }}
          >
            Add Task
          </Button>
        )}
      </EditorCard>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Section</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete section "{section.label}" and all its tasks?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={CANCEL_BTN_SX}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteConfirm(false);
              onDelete();
            }}
            color="error"
            variant="contained"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// --- Inline task editor ---

interface TaskEditFormProps {
  task?: TaskDefinition;
  /** Sibling tasks offered as taskOutcome sources (this task is filtered out). */
  taskOptions?: ConditionTaskOption[];
  onSave: (task: TaskDefinition) => void;
  onCancel: () => void;
}

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as const;

// Sentinel select value that opens the "new dropdown set" modal.
const CREATE_DROPDOWN = '__create__';

const TaskEditForm: React.FC<TaskEditFormProps> = ({ task, taskOptions, onSave, onCancel }) => {
  const { dropdownCatalog, refresh: refreshDropdowns } = useTaskDropdowns();
  const [title, setTitle] = useState(task?.title ?? '');
  const [taskId, setTaskId] = useState(task?.taskId ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [url, setUrl] = useState(task?.url ?? '');
  const [groupLabel, setGroupLabel] = useState(task?.groupLabel ?? '');
  const [noteRequired, setNoteRequired] = useState(task?.noteRequired ?? false);
  const [isDropdown, setIsDropdown] = useState(!!task?.dropdownId);
  const [dropdownId, setDropdownId] = useState(task?.dropdownId ?? '');
  const [showDropdownModal, setShowDropdownModal] = useState(false);
  const [showWhen, setShowWhen] = useState<TaskFieldCondition | undefined>(task?.showWhen);
  const [idManuallyEdited, setIdManuallyEdited] = useState(!!task);

  const isNew = !task;

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!idManuallyEdited) {
      setTaskId(slugify(value));
    }
  };

  const handleIdChange = (value: string) => {
    setTaskId(value);
    setIdManuallyEdited(true);
  };

  const trimmedTitle = title.trim();
  const trimmedId = taskId.trim();
  const trimmedUrl = url.trim();
  const urlInvalid = trimmedUrl.length > 0 && !/^https?:\/\//i.test(trimmedUrl);
  const isValid =
    trimmedTitle.length > 0 &&
    trimmedId.length > 0 &&
    !urlInvalid &&
    (!isDropdown || dropdownId.length > 0);

  const handleSubmit = () => {
    if (!isValid) return;
    onSave({
      taskId: trimmedId,
      title: trimmedTitle,
      description: description.trim() || undefined,
      url: trimmedUrl || undefined,
      groupLabel: groupLabel.trim() || undefined,
      noteRequired: noteRequired || undefined,
      dropdownId: isDropdown ? dropdownId : undefined,
      showWhen,
    });
  };

  return (
    <div className="editor-subform">
      <div className="editor-field-row">
        <FormField label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            /* eslint-disable-next-line jsx-a11y/no-autofocus */
            autoFocus={isNew}
          />
        </FormField>
        <FormField label="Task ID">
          <input
            type="text"
            value={taskId}
            onChange={(e) => handleIdChange(e.target.value)}
            readOnly={!isNew}
            style={MONO}
          />
        </FormField>
      </div>
      <div className="editor-hint">
        {isNew ? 'Auto-generated from title' : 'Set at creation'}
      </div>

      <FormField label="Group Label">
        <input
          type="text"
          value={groupLabel}
          onChange={(e) => setGroupLabel(e.target.value)}
          placeholder="Optional visual subheading"
        />
      </FormField>

      <FormField label="Description">
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </FormField>

      <FormField label="Link URL">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Optional — https://…"
        />
      </FormField>
      {urlInvalid && (
        <div className="editor-hint" style={{ color: '#c0392b' }}>
          URL must start with http:// or https://
        </div>
      )}

      <label
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#333', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          checked={noteRequired}
          onChange={(e) => setNoteRequired(e.target.checked)}
          style={{ width: 'auto' }}
        />
        <span>Note required to complete this task</span>
      </label>

      <FormField label="Completion Control">
        <select
          value={isDropdown ? 'dropdown' : 'checkbox'}
          onChange={(e) => setIsDropdown(e.target.value === 'dropdown')}
        >
          <option value="checkbox">Checkbox</option>
          <option value="dropdown">Dropdown</option>
        </select>
      </FormField>

      {isDropdown && (
        <FormField label="Dropdown Option Set">
          <select
            value={dropdownId}
            onChange={(e) => {
              if (e.target.value === CREATE_DROPDOWN) {
                setShowDropdownModal(true);
              } else {
                setDropdownId(e.target.value);
              }
            }}
          >
            <option value="">— Select a dropdown —</option>
            {dropdownCatalog.dropdowns.map((d) => (
              <option key={d.dropdownId} value={d.dropdownId}>
                {d.displayName} ({d.options.length} options)
              </option>
            ))}
            <option value={CREATE_DROPDOWN}>＋ Create new dropdown set…</option>
          </select>
        </FormField>
      )}

      <TaskDropdownFormDialog
        open={showDropdownModal}
        onClose={() => setShowDropdownModal(false)}
        onSaved={(saved) => {
          setShowDropdownModal(false);
          refreshDropdowns();
          setDropdownId(saved.dropdownId);
        }}
      />

      <ConditionEditor
        value={showWhen}
        onChange={(next) => setShowWhen(next == null ? undefined : (next as TaskFieldCondition))}
        allowCustom={false}
        allowTaskOutcome
        taskOptions={(taskOptions ?? []).filter((o) => o.taskId !== trimmedId)}
        label="Task Show When"
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" disableElevation onClick={handleSubmit} disabled={!isValid} sx={PRIMARY_BTN_SX}>
          {isNew ? 'Add Task' : 'Update'}
        </Button>
        <Button onClick={onCancel} sx={CANCEL_BTN_SX}>Cancel</Button>
      </Box>
    </div>
  );
};
